import { assertNonEmpty } from '../shared/branded-id';

export type CompanyId = string & { readonly __brand: 'CompanyId' };

export function CompanyId(raw: string): CompanyId {
  return assertNonEmpty(raw.trim(), raw, 'CompanyId') as CompanyId;
}

/** Every value the status column carries. `indexed` is bulk-import residue, not a pipeline stage. */
export const LEAD_STATUSES = [
  'lead',
  'contacted',
  'demo',
  'customer',
  'past_customer',
  'indexed',
] as const;

export type LeadStatus = (typeof LEAD_STATUSES)[number];

/** The statuses the filter panel offers. `indexed` is deliberately not one of them. */
export const PIPELINE_STATUSES: readonly LeadStatus[] = [
  'lead',
  'contacted',
  'demo',
  'customer',
  'past_customer',
];

/** Statuses meaning someone has already worked this entity, which is what the group rail warns about. */
const ACTIVITY_STATUSES: ReadonlySet<string> = new Set([
  'contacted',
  'demo',
  'customer',
  'past_customer',
]);

export function isActivityStatus(status: string | null): boolean {
  return status !== null && ACTIVITY_STATUSES.has(status);
}

/**
 * `signal_ratio`'s stand-in for an infinite ratio: online signal against zero or absent
 * retail. Stored rather than computed so PostgREST can sort and threshold on it.
 */
export const RATIO_SENTINEL = 999999;

export function isInfiniteRatio(ratio: number | null): boolean {
  return ratio !== null && ratio >= RATIO_SENTINEL;
}

export type BrandEdgeRole = 'owner' | 'distributor' | 'unknown';

/** The roles a brand edge can carry, in the order the drawer offers them. */
export const BRAND_EDGE_ROLES: readonly BrandEdgeRole[] = ['owner', 'distributor', 'unknown'];

export interface PersonEdge {
  readonly name: string | null;
  readonly title: string | null;
  readonly email: string | null;
  readonly linkedin: string | null;
  readonly source: string | null;
}

export interface BrandEdge {
  readonly raw: string | null;
  readonly portalName: string | null;
  readonly role: BrandEdgeRole;
  readonly source: string | null;
  /** The three brandb values, null when the brand is not in brandb. */
  readonly brandname: string | null;
  readonly brandalias: string | null;
  readonly brandurl: string | null;
  readonly products: string | null;
  readonly cats: string | null;
}

/**
 * One portal comparison holding at least one product of this company's brands. Recomputed
 * server-side by `aposell_refresh_comparisons`; never edited from the frontend.
 */
export interface ComparisonEdge {
  /** The `portal_comparison` uuid. */
  readonly id: string;
  readonly name: string | null;
  readonly productsTotal: number | null;
  readonly companyProductsN: number | null;
  /** Share of the company's own portal products, null when it has none. */
  readonly companyProductsPct: number | null;
  /** The company's brands present in this comparison, biggest first; catalogue display names. */
  readonly companyBrands: readonly string[];
}

export interface LeadCompany {
  readonly companyId: CompanyId;
  readonly name: string | null;
  readonly nameVariations: string | null;
  readonly nameNorm: string | null;
  readonly groupKey: string | null;
  readonly groupManual: string | null;
  readonly country: string | null;
  readonly domain: string | null;
  readonly orgVat: string | null;
  readonly orgNr: string | null;
  readonly status: LeadStatus | null;
  readonly clickupStatus: string | null;
  readonly portalOrgId: string | null;
  readonly owner: string | null;
  readonly lastContactedPerson: string | null;
  readonly nextAction: string | null;
  readonly language: string | null;
  readonly linkedinUrl: string | null;
  readonly revenueRaw: string | null;
  readonly mainCategories: string | null;
  readonly roles: string | null;
  readonly memberships: string | null;
  readonly notes: string | null;
  readonly sources: string | null;
  readonly reviewFlag: string | null;
  readonly signalPeriod: string | null;
  readonly doNotContact: boolean | null;
  readonly lastContacted: string | null;
  readonly nextActionDate: string | null;
  readonly foundedYear: number | null;
  readonly revenueSek: number | null;
  readonly employees: number | null;
  readonly portalProductsN: number | null;
  readonly brandsInPortalN: number | null;
  readonly productsNonrxN: number | null;
  readonly priority: number | null;
  readonly signalRevenueOnline: number | null;
  readonly signalRevenueRetail: number | null;
  readonly signalVendorsOnline: number | null;
  readonly signalVendorsRetail: number | null;
  readonly signalRatio: number | null;
  readonly hasPersonEmail: boolean | null;
  /** Generated from `comparisons`, so sortable and filterable server-side. */
  readonly comparisonsN: number | null;
  /** Highest `company_products_pct` across `comparisons`; null when there are none. */
  readonly topComparisonPct: number | null;
  readonly persons: readonly PersonEdge[];
  readonly brands: readonly BrandEdge[];
  /** Kept in the order the function produced: company_products_n descending. */
  readonly comparisons: readonly ComparisonEdge[];
}

/** Splits one of the joined text columns; `sep` is `;` for most and `|` for review_flag. */
export function splitJoined(value: string | null, sep: string): readonly string[] {
  if (!value) return [];
  return value
    .split(sep)
    .map((part) => part.trim())
    .filter((part) => part.length > 0);
}

/** Name, else email, else title, else the LinkedIn URL: the first thing that identifies them. */
export function personLabel(person: PersonEdge): string {
  return (
    (person.name ?? '').trim() ||
    (person.email ?? '').trim() ||
    (person.title ?? '').trim() ||
    (person.linkedin ?? '').trim() ||
    '(unnamed)'
  );
}

export function personEmailCount(persons: readonly PersonEdge[]): number {
  return persons.filter((person) => (person.email ?? '').trim().length > 0).length;
}

/** The brands table shows brandalias when brandb knows the brand, and the source spelling otherwise. */
export function brandDisplayName(edge: BrandEdge): string {
  return (edge.brandalias ?? '').trim() || (edge.raw ?? '').trim() || '(unnamed)';
}

/**
 * The `map_focus_brand` for a sample report: the company's biggest brand IN THIS comparison.
 *
 * `company_brands` is written by `aposell_refresh_comparisons` from the catalogue rows the
 * comparison's own products join to, ordered by product count, so the name cannot be a brand
 * the comparison does not contain. Empty means send none: the map then highlights the
 * comparison's best seller, which is a better failure than naming an absent brand.
 */
export function comparisonFocusBrand(edge: ComparisonEdge): string | null {
  return edge.companyBrands[0] ?? null;
}

export function isInBrandb(edge: BrandEdge): boolean {
  return (edge.brandname ?? '').trim().length > 0;
}

const TRANSLITERATIONS: Readonly<Record<string, string>> = {
  å: 'a',
  ä: 'a',
  ö: 'o',
  æ: 'a',
  ø: 'o',
  ü: 'u',
  é: 'e',
  è: 'e',
  á: 'a',
  à: 'a',
  ó: 'o',
  ò: 'o',
  í: 'i',
  ì: 'i',
  ñ: 'n',
  ç: 'c',
};

/** Lowercased, the accented letters folded, trimmed, whitespace runs collapsed to one space. */
function foldName(name: string): string {
  return name
    .toLowerCase()
    .replace(/[åäöæøüéèáàóòíìñç]/g, (letter) => TRANSLITERATIONS[letter] ?? letter)
    .trim()
    .replace(/\s+/g, ' ');
}

/** `name_norm` as the scripts derive it (user ruling 2026-09-16): the folded name without commas and periods. */
export function nameNorm(name: string): string {
  return foldName(name).replace(/[,.]/g, '');
}

/** Legal-form words a group key ignores; whole words only, after folding. */
const LEGAL_FORM_WORDS: ReadonlySet<string> = new Set([
  'ab',
  'a/s',
  'as',
  'aps',
  'oy',
  'oyj',
  'gmbh',
  'ltd',
  'limited',
  'inc',
  'llc',
  'corp',
  'corporation',
  'sa',
  'sas',
  'spa',
  'srl',
  'bv',
  'nv',
  'ag',
  'kg',
  'co',
  'company',
  'holding',
  'group',
  'international',
  'aktiebolag',
  'aktieselskab',
  'aksjeselskap',
  'anpartsselskab',
  'osakeyhtio',
]);

/** Region words a group key also ignores, unless dropping them leaves too little to key on. */
const REGION_WORDS: ReadonlySet<string> = new Set([
  'sweden',
  'sverige',
  'scandinavia',
  'nordic',
  'nordics',
  'norden',
  'danmark',
  'denmark',
  'norway',
  'norge',
  'finland',
  'europe',
]);

/** Below this many characters a key says nothing, so the region words are kept. */
const GROUP_KEY_MIN_LENGTH = 4;

function keyOf(words: readonly string[]): string {
  return words.join('').replace(/[^a-z0-9]/g, '');
}

/**
 * The `group_key` for a company created without one (user ruling 2026-09-16): the folded
 * name with the legal-form and region words removed, reduced to a-z and 0-9. When that is
 * shorter than four characters the region words are kept, so "Nordic AB" keys as "nordic".
 */
export function groupKeyFromName(name: string): string {
  const words = foldName(name)
    .split(' ')
    .filter((word) => word.length > 0 && !LEGAL_FORM_WORDS.has(word));
  const withoutRegions = keyOf(words.filter((word) => !REGION_WORDS.has(word)));
  return withoutRegions.length >= GROUP_KEY_MIN_LENGTH ? withoutRegions : keyOf(words);
}

/**
 * The portal slug of a brand name as the aposell edges carry it (user ruling 2026-09-16):
 * lowercase, å and ä to a, ö to o, whitespace to hyphen, everything else that is not
 * alphanumeric or a hyphen dropped. '' when nothing survives.
 */
export function brandSlugFromName(name: string): string {
  return name
    .trim()
    .toLowerCase()
    .replace(/[åä]/g, 'a')
    .replace(/ö/g, 'o')
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9-]/g, '');
}

/**
 * The bare host of a stored domain: protocol, `www.`, port, path and case removed, so
 * `https://www.Renapharma.se/om-oss` and `renapharma.se` are the same company. null when
 * nothing that looks like a host is left.
 */
export function bareDomain(raw: string | null): string | null {
  if (!raw) return null;
  const host = raw
    .trim()
    .toLowerCase()
    .replace(/^[a-z]+:\/\//, '')
    .replace(/^www\./, '')
    .split(/[/?#:]/)[0]
    ?.trim();
  return host?.includes('.') ? host : null;
}

/** The Apollo people search for a company's domain; null when the company has no usable domain. */
export function apolloSearchUrl(domain: string | null): string | null {
  const host = bareDomain(domain);
  if (host === null) return null;
  return `https://app.apollo.io/#/people?page=1&qOrganizationDomains=${encodeURIComponent(host)}`;
}

/**
 * What the create form collects (user ruling 2026-09-16). `name`, `country` and `domain` are
 * required; every other field is written only when set. Sources is not the form's to choose:
 * a new company is always manually sourced, and its status is the table's default.
 */
export interface LeadCompanyDraft {
  readonly name: string;
  /** Two-letter code, upper case, as the country buckets read it. */
  readonly country: string;
  /** Already reduced with `bareDomain`. */
  readonly domain: string;
  readonly nameVariations: string | null;
  /** An existing company's group key, or a new one; null means derive it from the name. */
  readonly groupKey: string | null;
  readonly orgNr: string | null;
  readonly orgVat: string | null;
  readonly foundedYear: number | null;
  readonly revenueSek: number | null;
  readonly revenueRaw: string | null;
  readonly employees: number | null;
  readonly linkedinUrl: string | null;
  readonly language: string | null;
  readonly mainCategories: string | null;
  readonly roles: string | null;
  readonly memberships: string | null;
  readonly notes: string | null;
}

/** The three identity values brandb holds for a brand; null when brandb has no such row. */
export interface BrandbEntry {
  readonly brandname: string;
  readonly brandalias: string | null;
  readonly brandurl: string | null;
}

/** A company whose group key the create form can reuse. */
export interface GroupSuggestion {
  readonly companyId: CompanyId;
  readonly name: string | null;
  readonly groupKey: string;
}

/**
 * The brand edge the drawer adds (user ruling 2026-09-16): the entered spelling as `raw`, the
 * computed portal slug, the chosen role, source `manual`, and brandb's three values when
 * brandb knows the slug. `products` and `cats` are the scripts' to fill.
 */
export function manualBrandEdge(
  name: string,
  role: BrandEdgeRole,
  brandb: BrandbEntry | null
): BrandEdge {
  return {
    raw: name.trim(),
    portalName: brandSlugFromName(name),
    role,
    source: 'manual',
    brandname: brandb?.brandname ?? null,
    brandalias: brandb?.brandalias ?? null,
    brandurl: brandb?.brandurl ?? null,
    products: null,
    cats: null,
  };
}

/**
 * The comparison covering the largest share of the company's own portal products.
 *
 * `comparisons` arrives sorted by `company_products_n` descending, which is a different
 * question: a 400-product comparison can hold more of the company's SKUs than a targeted one
 * while covering a smaller share of them. Coverage is what says whether a comparison is
 * actually aimed at this company, so it is scanned for rather than read off the front.
 */
export function topComparisonByCoverage(
  comparisons: readonly ComparisonEdge[]
): ComparisonEdge | null {
  let best: ComparisonEdge | null = null;
  for (const edge of comparisons) {
    if (edge.companyProductsPct === null) continue;
    if (best === null || edge.companyProductsPct > (best.companyProductsPct ?? 0)) best = edge;
  }
  return best;
}
